import type { Branded } from '@library/type-utils'

export type RecipeUUID = Branded<string, 'RecipeUUID'>
export type RecipeURN = Branded<string, 'RecipeURN'>
export type MenuURN = Branded<string, 'MenuURN'>
export type RecipeCoreId = Branded<string, 'RecipeCoreId'>

export type RecipeDietaryClaim = {
  name: string
  slug: string
}

export type RecipeImage = {
  type: string
  crops: {
    width: number
    url: string
  }[]
}

export type RecipeDietType = {
  name: string
  slug: string
}

export type RecipePortion = {
  numPortions: number
  isAvailable: boolean
}

export type Recipe = {
  id: RecipeUUID
  /**
   * @deprecated Use `id` field when possible
   */
  coreId: RecipeCoreId
  name: string
  dietaryClaims: RecipeDietaryClaim[]
  /**
   * @deprecated API currently doesn't return this field, add it if necessary to menuapiv3, menupreviewapiv3
   */
  dietType: RecipeDietType | null
  fiveADay: number | null
  foodBrand: {
    slug: string
    name: string
  } | null
  images: RecipeImage[]
  invalidPreference: string | null
  isAvailable: boolean
  isOffered: boolean
  merchandisingTags: {
    availability: {
      name: string
      color: {
        light: string
        dark: string
      }
      backgroundColor: {
        light: string
        dark: string
      }
    } | null
    tagline: {
      name: string
      color: {
        light: string
        dark: string
      }
      backgroundColor: {
        light: string
        dark: string
      }
    } | null
  }
  nutritionalInformation: {
    perPortion: {
      energyKcal: number
      carbsMg: number
      fatMg: number
      fibreMg: number | null
      proteinMg: number
      saltMg: number
    }
  }
  prepTime: number | null
  portions?: RecipePortion[]
  preparationType?: string | null
  rating: {
    average: number
    count: number
  }
  shelfLife: {
    minDays: number
    maxDays: number
  } | null
  spiceLevel: {
    name: string
  } | null
  surcharge: {
    name: string
    price: number
    pricePerPortion: number
  } | null
}

export type CollectionUUID = Branded<string, 'CollectionUUID'>
export type CollectionSlug = Branded<string, 'CollectionSlug'>

export type CollectionMerchandisingImage = {
  crops: {
    width: number
    url: string
  }[]
  type: string
}

export type CollectionMerchandisingHeaderImage = {
  width: number
  height: number
  url: string
  altText: string
}

export type CollectionMerchandisingItem =
  | {
      type: 'recipe'
      id: RecipeUUID
    }
  | {
      type: 'cta'
      title: string
      ctaText: string
      targetCategoryId: string
      targetCategorySlug: string
    }

export type CategoryBrandingMerchandising = {
  type: 'category-branding'
  backgroundImage: CollectionMerchandisingImage
  backgroundColor: string
  textColor: string
  title: string
  description: string
}

export type CampaignBannerMerchandising = {
  type: 'campaign-banner'
  backgroundImage: CollectionMerchandisingImage
  backgroundColor: string
  textColor: string
  title: string
  description: string
  subtitle: string
  items: CollectionMerchandisingItem[]
}

export type CompactCampaignBannerMerchandising = {
  type: 'compact-campaign-banner'
  backgroundImage: CollectionMerchandisingImage
  backgroundColor: string
  textColor: string
  title: string
  description: string
  ctaText: string
  targetCategoryId: string
  targetCategorySlug: string
  items: Array<{
    type: 'recipe'
    id: RecipeUUID
  }>
}

export type WaveLinkHeaderMerchandising = {
  type: 'wave-link-header'
  title: string
  headerImage: CollectionMerchandisingHeaderImage[]
  description: string
  color: string
  backgroundColor: string
  collectionSlug?: string
  collectionId?: string
}

export type GradientInfoHeaderMerchandising = {
  type: 'gradient-info-header'
  title: string
  headerImage: CollectionMerchandisingHeaderImage[]
  description: string
  color: string
  gradientColor: string
  image: CollectionMerchandisingHeaderImage[]
  imageLocation: 'left' | 'right'
}

export type CollectionMerchandising =
  | CategoryBrandingMerchandising
  | CampaignBannerMerchandising
  | CompactCampaignBannerMerchandising
  | WaveLinkHeaderMerchandising
  | GradientInfoHeaderMerchandising

export const SUPPORTED_OPTION_TYPES_INGREDIENTS = ['none', 'recipes', 'ingredients'] as const

export type SupportedOptionType = typeof SUPPORTED_OPTION_TYPES_INGREDIENTS[number]

export type Collection = {
  id: CollectionUUID
  slug: CollectionSlug
  shortTitle: string
  order: number
  merchandising?: CollectionMerchandising[]
  recipes: {
    id: RecipeUUID
    optionsType: SupportedOptionType
    /**
     * @deprecated Use recipe details endpoint to get options instead
     */
    options: {
      id: RecipeUUID
    }[]
  }[]
  index: number
}

export type ParentCategory = {
  id: CollectionUUID
  slug: CollectionSlug
  name?: string
  description?: string
  isLinkable: boolean
  type: 'generic' | 'promoted'
  recipeCount: number
  categories: {
    id: CollectionUUID
    slug: CollectionSlug
    shortTitle: string
    recipeCount: number
    image?: {
      crops: {
        url: string
        width: number
      }[]
      type: string
    }
  }[]
}

export type GroupUUID = Branded<string, 'GroupUUID'>
export type GroupSlug = Branded<string, 'GroupSlug'>

export type Group = {
  displayType: string
  description?: string
  id: string
  order: number
  recipeCount: number
  recipes: {
    id: RecipeUUID
    options: {
      id: RecipeUUID
    }[]
  }[]
  title: string
  slug: string
}
